import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User } from '@/types'
import { getSupabaseClient } from '@/utils/supabase'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!user.value)

  const login = async (email: string, password: string) => {
    loading.value = true
    try {
      console.log('[Auth] 开始登录:', { email, password: password ? '***' : 'empty' })
      
      const supabase = getSupabaseClient()
      console.log('[Auth] Supabase 客户端已获取')
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      console.log('[Auth] 登录响应:', { data: data ? 'success' : 'no data', error })

      if (error) {
        console.error('[Auth] 登录错误:', error)
        throw error
      }

      // 获取用户详细信息
      if (data.user) {
        console.log('[Auth] 用户登录成功，加载用户信息:', data.user.id)
        await loadUser(data.user.id)
      }

      return { success: true }
    } catch (error: any) {
      console.error('[Auth] 登录失败:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const register = async (email: string, password: string, username: string) => {
    loading.value = true
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
          },
        },
      })

      if (error) throw error

      return { success: true }
    } catch (error: any) {
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      const supabase = getSupabaseClient()
      await supabase.auth.signOut()
      user.value = null
      return { success: true }
    } catch (error: any) {
      return { success: false, error: error.message }
    }
  }

  const loadUser = async (userId?: string) => {
    try {
      const supabase = getSupabaseClient()
      
      if (!userId) {
        const { data: { user: authUser } } = await supabase.auth.getUser()
        if (!authUser) return
        userId = authUser.id
      }

      console.log('[Auth] 加载用户信息:', userId)

      let { data: userData, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single()

      // 如果用户不存在（PGRST116 错误），自动创建
      if (error && error.code === 'PGRST116') {
        console.log('[Auth] 用户不存在，自动创建...')
        const { data: { user: authUser } } = await supabase.auth.getUser()
        
        if (authUser) {
          const username = authUser.user_metadata?.name || 
                          authUser.user_metadata?.full_name || 
                          authUser.email?.split('@')[0] || 
                          '用户'
          
          console.log('[Auth] 创建用户:', {
            id: authUser.id,
            email: authUser.email,
            username
          })

          // 使用 RPC 调用来绕过 RLS 策略
          // 因为 RLS 策略要求 auth.uid() = id，但在客户端无法满足
          const { error: insertError } = await supabase.rpc('create_user_profile', {
            user_id: authUser.id,
            user_email: authUser.email,
            user_username: username
          })
          
          if (insertError) {
            console.error('[Auth] 创建用户失败:', insertError)
            // 如果 RPC 函数不存在，尝试直接插入
            console.log('[Auth] 尝试直接插入...')
            const { data: directInsert, error: directError } = await supabase
              .from('users')
              .insert({
                id: authUser.id,
                email: authUser.email,
                username,
                password_hash: 'oauth_user_no_password',
                role: 'user',
                status: 'active'
              })
              .select()
              .single()
            
            if (directError) {
              console.error('[Auth] 直接插入也失败:', directError)
              throw directError
            }
            userData = directInsert
          } else {
            // RPC 成功，查询用户数据
            const { data: fetchedUser } = await supabase
              .from('users')
              .select('*')
              .eq('id', authUser.id)
              .single()
            userData = fetchedUser
          }
          
          console.log('[Auth] 用户创建成功:', userData)
        }
      } else if (error) {
        console.error('[Auth] 查询用户失败:', error)
        throw error
      }

      user.value = userData
      console.log('[Auth] 用户信息已加载:', user.value)
    } catch (error) {
      console.error('[Auth] 加载用户信息失败:', error)
      user.value = null
    }
  }

  const checkAuth = async () => {
    loading.value = true
    try {
      const supabase = getSupabaseClient()
      const { data: { user: authUser } } = await supabase.auth.getUser()
      
      if (authUser) {
        await loadUser(authUser.id)
      }
    } catch (error) {
      console.error('检查认证状态失败:', error)
    } finally {
      loading.value = false
    }
  }

  const updatePassword = async (newPassword: string) => {
    loading.value = true
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error

      return { success: true }
    } catch (error: any) {
      console.error('修改密码失败:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const requestPasswordReset = async (email: string) => {
    loading.value = true
    try {
      const supabase = getSupabaseClient()
      // 优先使用环境变量配置的 URL，如果没有则使用当前域名
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appUrl}/reset-password`,
      })

      if (error) throw error

      return { success: true }
    } catch (error: any) {
      console.error('发送密码重置邮件失败:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const resetPassword = async (newPassword: string) => {
    loading.value = true
    try {
      const supabase = getSupabaseClient()
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (error) throw error

      return { success: true }
    } catch (error: any) {
      console.error('重置密码失败:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  // 支付宝 OAuth 登录
  const loginWithAlipay = async () => {
    loading.value = true
    try {
      console.log('[Auth] 开始支付宝登录')
      
      const supabase = getSupabaseClient()
      
      // 检测是否为移动设备
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const isAlipayBrowser = /AlipayClient/i.test(navigator.userAgent)
      
      console.log('[Auth] 设备检测:', { isMobile, isAlipayBrowser })
      
      // 所有环境统一使用标准 OAuth 流程
      // 移动端：支付宝会自动检测并提示"在支付宝客户端打开"，用户点击后会在 APP 中打开
      // 支付宝内：直接显示授权页面
      // PC 端：显示二维码或登录页面
      // 
      // 关键：不要使用 alipays:// 协议包装 URL，让支付宝自己处理跳转
      // 授权完成后，支付宝会根据 redirect_uri 自动返回浏览器
      console.log('[Auth] 使用标准 OAuth 流程（支付宝会自动处理移动端跳转）')
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'alipay' as any
      })

      if (error) {
        console.error('[Auth] 支付宝登录错误:', error)
        throw error
      }

      console.log('[Auth] 支付宝登录跳转中...', data)
      
      // 移动端额外提示
      if (isMobile && !isAlipayBrowser) {
        console.log('[Auth] 移动端提示：页面会显示"在支付宝客户端打开"，点击后会自动跳转到支付宝 APP')
      }
      
      return { success: true, data }
    } catch (error: any) {
      console.error('[Auth] 支付宝登录失败:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  // 微信 OAuth 登录
  const loginWithWechat = async () => {
    loading.value = true
    try {
      console.log('[Auth] 开始微信登录')
      
      const supabase = getSupabaseClient()
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'wechat' as any
        // 不指定 options，让 Supabase 使用默认配置
        // 这样可以避免 AUTH_REDIRECT_URL_NOT_IN_WHITE 错误
      })

      if (error) {
        console.error('[Auth] 微信登录错误:', error)
        throw error
      }

      console.log('[Auth] 微信登录跳转中...', data)
      return { success: true, data }
    } catch (error: any) {
      console.error('[Auth] 微信登录失败:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const startWechatQrLogin = async () => {
    loading.value = true
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      const res = await fetch(`${supabaseUrl}/functions/v1/wx-qr-create`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'apikey': anonKey, 'Authorization': `Bearer ${anonKey}` },
        body: JSON.stringify({})
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.error || 'create_qr_session_failed')
      const qrCodeUrl = `${supabaseUrl}${data.qrCodeUrl}`
      return { success: true, sessionId: data.sessionId, qrCodeUrl }
    } catch (error: any) {
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const pollWechatQrLogin = async (sessionId: string) => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      const res = await fetch(`${supabaseUrl}/functions/v1/wx-qr-status`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'apikey': anonKey, 'Authorization': `Bearer ${anonKey}` },
        body: JSON.stringify({ sessionId })
      })
      const data = await res.json()
      if (!data.ok) return { success: false, status: 'error', error: data.error }
      if (data.status === 'authenticated') {
        const openid = data.openid || 'unknown'
        const now = new Date().toISOString()
        user.value = {
          id: `wx:${openid}`,
          username: '微信用户',
          email: `${openid}@wechat.local`,
          role: 'user',
          status: 'active',
          created_at: now,
          updated_at: now,
          avatar_url: null,
          activated_cards: []
        } as any
        localStorage.setItem('WX_QR_TOKEN', data.token || '')
        return { success: true, status: 'authenticated' }
      }
      return { success: true, status: data.status }
    } catch (error: any) {
      return { success: false, status: 'error', error: error.message }
    }
  }

  return {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    loadUser,
    checkAuth,
    updatePassword,
    requestPasswordReset,
    resetPassword,
    loginWithAlipay,
    loginWithWechat,
    startWechatQrLogin,
    pollWechatQrLogin,
  }
})
